import {DTO} from "../../../../common/utils/types";
import React, {Fragment} from "react";
import "../download-files.scss"
import {DownloadFile} from "../../../../components/download-file/DownloadFile";

export class FileDownloadDetails {
    type: string
    displayName: string
    fileName: string

    constructor(fileDownloadDetails: DTO<FileDownloadDetails>) {
        this.fileName = fileDownloadDetails.fileName
        this.displayName = fileDownloadDetails.displayName
        this.type = fileDownloadDetails.type
    }

    render(appName: string, reviewName: String | null = null, sectionName: string): React.ReactNode {
        return (
            <>
                <DownloadFile
                    file={{
                        fileNameSpace: appName,
                        fileCategory: this.type,
                        fileName: this.fileName
                    }}
                    displayName={this.displayName}
                    matomo={{reviewName: reviewName, sectionName}}
                />
                <div className="padding-container"/>
            </>
        )
    }
}

export class ReviewFiles {
    dumpFile: FileDownloadDetails | null
    evidence: FileDownloadDetails | null

    constructor(reviewFiles: DTO<ReviewFiles>) {
        this.dumpFile = reviewFiles.dumpFile ? new FileDownloadDetails(reviewFiles.dumpFile) : null
        this.evidence = reviewFiles.evidence ? new FileDownloadDetails(reviewFiles.evidence) : null
    }

    hasFiles(): boolean {
        return this.dumpFile !== null || this.evidence !== null
    }

    private renderExtraFiles(extraDownloadableFiles: Array<FileDownloadDetails> | null, appName, reviewName: String | null = null, sectionName: string) {
        return (
            <>
                {extraDownloadableFiles ?
                    extraDownloadableFiles.map((extraDownloadableFile) => {
                        return (
                            <Fragment key={extraDownloadableFile.type}>
                                {extraDownloadableFile.render(appName, reviewName, sectionName)}
                            </Fragment>
                        )
                    })
                    : null
                }
            </>
        );
    }

    render(
        appName: string,
        sectionName: string,
        extraDownloadableFiles: Array<FileDownloadDetails> | null = null,
        reviewName: String | null = null
    ): React.ReactNode {
        return (
            <>
                {(this.hasFiles() || extraDownloadableFiles !== null) ?
                    <>
                        <div className="application-review-label">
                            {sectionName}
                        </div>
                        <div className="padding-container"/>
                        <div className="downloadable-files">
                            {this.dumpFile?.render(appName, reviewName, sectionName) ?? null}
                            {this.evidence?.render(appName, reviewName, sectionName) ?? null}
                            {this.renderExtraFiles(extraDownloadableFiles, appName, reviewName, sectionName)}
                        </div>
                        <div className="padding-container"/>
                    </> : null}
            </>
        )
    }
}

export class UserAccesses {
    forReview: ReviewFiles | null
    forVerification: ReviewFiles | null
    forUpload: ReviewFiles | null

    constructor(userAccesses: DTO<UserAccesses>) {
        this.forReview = userAccesses.forReview ? new ReviewFiles(userAccesses.forReview) : null
        this.forVerification = userAccesses.forVerification ? new ReviewFiles(userAccesses.forVerification) : null
        this.forUpload = userAccesses.forUpload ? new ReviewFiles(userAccesses.forUpload) : null
    }

    latestUserAccess(): { dumpFile: FileDownloadDetails | null, evidence: FileDownloadDetails | null } {
        if (this.forUpload?.hasFiles()) {
            return {dumpFile: this.forUpload?.dumpFile, evidence: this.forUpload?.evidence}
        }
        if (this.forVerification?.hasFiles()) {
            return {dumpFile: this.forVerification?.dumpFile, evidence: this.forVerification?.evidence}
        }
        if (this.forReview?.hasFiles()) {
            return {dumpFile: this.forReview?.dumpFile, evidence: this.forReview?.evidence}
        }
        return {dumpFile: null, evidence: null}
    }

    hasFiles(): boolean {
        function hasFiles(reviewFiles: ReviewFiles | null): boolean {
            return reviewFiles !== null && reviewFiles.hasFiles()
        }

        return hasFiles(this.forReview) || hasFiles(this.forVerification) || hasFiles(this.forUpload)
    }
}

export class ApplicationDownloadableFiles {
    userAccesses: UserAccesses
    accessOwnerConditions: FileDownloadDetails | null

    constructor(applicationDownloadableFiles: DTO<ApplicationDownloadableFiles>) {
        this.accessOwnerConditions = applicationDownloadableFiles.accessOwnerConditions ?
            new FileDownloadDetails(applicationDownloadableFiles.accessOwnerConditions) : null
        this.userAccesses = new UserAccesses(applicationDownloadableFiles.userAccesses)
    }

    hasFiles(): boolean {
        return this.accessOwnerConditions !== null || this.userAccesses.hasFiles()
    }

    private renderFileNotAvailable(displayName: string): React.ReactNode {
        return (
            <>
                <DownloadFile
                    file={null}
                    displayName={displayName}
                    matomo={null}
                />
                <div className="padding-container"/>
            </>
        )
    }

    renderLatestFiles(appName: string): React.ReactNode {
        const latestUserAccesses = this.userAccesses.latestUserAccess()
        const sectionName = "Latest uploaded files";
        return (
            <>
                {this.hasFiles() ?
                    <div className="application-review-stage">
                        <div className="application-review-label">
                            {sectionName}
                        </div>
                        <div className="padding-container"/>
                        <div className="downloadable-files">
                            {
                                latestUserAccesses.dumpFile ?
                                    latestUserAccesses.dumpFile.render(appName, null, sectionName) :
                                    null
                            }
                            {
                                latestUserAccesses.evidence ?
                                    latestUserAccesses.evidence.render(appName, null, sectionName) :
                                    null
                            }
                            {
                                this.accessOwnerConditions ?
                                    this.accessOwnerConditions.render(appName, null, sectionName) :
                                    null
                            }
                        </div>
                        <div className="padding-container"/>
                    </div>
                    : null}
            </>
        )
    }

    render(appName: string, reviewName: String | null = null): React.ReactNode {
        return (
            <>
                {this.hasFiles() ?
                    <div className="application-review-stage">
                        <div className="padding-container"/>
                        <>
                            {
                                this.userAccesses.forReview?.render(
                                    appName,
                                    "Files uploaded for the review",
                                    this.accessOwnerConditions !== null ? [this.accessOwnerConditions] : null,
                                    reviewName
                                ) ?? null
                            }
                            {
                                this.userAccesses.forVerification?.render(
                                    appName,
                                    "Files uploaded for review verification",
                                    null,
                                    reviewName
                                ) ?? null
                            }
                            {
                                this.userAccesses.forUpload?.render(
                                    appName,
                                    "Files uploaded after review completion",
                                    null,
                                    reviewName
                                ) ?? null
                            }
                        </>
                    </div> : null}
            </>
        )
    }
}